import { useCallback, useRef, useState } from 'react';

const MIN_WIDTH = 50;

const load = (storageKey: string): Record<string, number> => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const save = (storageKey: string, widths: Record<string, number>) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch {
    // storage pieno o bloccato: la larghezza resta valida per la sessione
  }
};

/**
 * Larghezze colonna ridimensionabili a trascinamento, persistite in
 * localStorage sotto ``storageKey`` (una mappa ``id colonna → px``).
 * Le colonne mai ridimensionate usano il ``fallback`` passato a ``widthOf``.
 * Il salvataggio avviene al rilascio del puntatore, non a ogni movimento.
 */
export function useColumnWidths(storageKey: string) {
  const [widths, setWidths] = useState<Record<string, number>>(() => load(storageKey));
  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  const widthOf = useCallback((id: string, fallback: number) => widths[id] ?? fallback, [widths]);

  const startResize = useCallback((id: string, startWidth: number, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: PointerEvent) => {
      const next = Math.max(MIN_WIDTH, Math.round(startWidth + ev.clientX - startX));
      setWidths(prev => (prev[id] === next ? prev : { ...prev, [id]: next }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
      save(storageKey, widthsRef.current);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [storageKey]);

  // Doppio click sulla maniglia: torna alla larghezza predefinita.
  const resetWidth = useCallback((id: string) => {
    setWidths(prev => {
      const { [id]: _, ...rest } = prev;
      save(storageKey, rest);
      return rest;
    });
  }, [storageKey]);

  return { widthOf, startResize, resetWidth };
}
